import type { AxiosError } from 'axios'
import { type UseFetchError } from '~/types/Error'
import { BaseError } from '~/errors/BaseError'

export interface NetworkErrorConstructor {
  statusCode: number | 'Unknown'
  error: string
  message: string
  timestamp?: string | number
  cause?: unknown
}

export class NetworkError extends BaseError {
  statusCode: number | 'Unknown'

  constructor(errorObj: NetworkErrorConstructor) {
    super(
      errorObj.message,
      errorObj.error,
      errorObj.timestamp || new Date().toISOString(),
    )
    this.name = 'NetworkError'
    this.statusCode = errorObj.statusCode
    this.cause = errorObj.cause
  }

  static fromUseFetchError(errorObj: UseFetchError) {
    const statusCode =
      errorObj.data?.statusCode || errorObj.statusCode || 'Unknown'

    const error =
      errorObj.data?.error || errorObj.statusMessage || 'Unknown Status'

    let message = `${statusCode.toString()}: ${error}`
    if (errorObj.message) {
      message += `\nFetchError: ${errorObj.message}`
    }
    if (errorObj.data?.message) {
      const serverMsg =
        errorObj.data.message instanceof Array
          ? errorObj.data.message.join(', ')
          : errorObj.data.message
      message += `\nServerMessage: ${serverMsg}`
    }
    if (errorObj.cause) {
      message += `\nCause: ${errorObj.cause}`
    }

    const timestamp = errorObj.data?.timestamp || new Date().toISOString()

    return new NetworkError({
      statusCode,
      error,
      message,
      timestamp,
    })
  }

  static fromAxiosError(errorObj: AxiosError) {
    const statusCode = errorObj.response?.status || 'Unknown'
    const error =
      errorObj.response?.data?.error || errorObj.name || 'Unknown Network Error'
    const message = `${statusCode.toString()}: ${error}`

    return new NetworkError({
      statusCode,
      error,
      message,
      cause: errorObj,
    })
  }
}
